/**
 * 支出の入力フォーム。
 *
 * 入力の負担を減らすため、支出名を打ち始めた時点で「よくある支出」の候補を表示する
 * (過去に入力した支出は候補に出さない)。
 * フォームの状態遷移そのものは @spli/app-logic の純粋関数に切り出してあり、
 * ここでは「見た目」と「イベントの繋ぎ込み」だけを担当する。
 */
import {
  applySuggestion,
  buildExpenseInput,
  computeShares,
  createEmptyForm,
  toggleParticipant,
  useSuggestions,
  type ExpenseFormState,
  type SplitMode,
} from "@spli/app-logic";
import {
  formatMoney,
  parseAmountInput,
  type ExpenseInput,
  type ExpenseSuggestion,
  type GroupDto,
} from "@spli/core";
import { useMemo, useRef, useState, type FormEvent, type KeyboardEvent } from "react";
import { useDebouncedValue } from "../hooks/useDebouncedValue.js";
import { SuggestionList } from "./SuggestionList.js";

const SPLIT_MODE_LABELS: Record<SplitMode, string> = {
  equal: "均等",
  adjust: "個別に調整",
  shares: "比率で調整",
};

/** 分け方ごとの、内訳表に出す入力欄の説明 */
const SPLIT_MODE_HINTS: Record<SplitMode, string> = {
  equal: "",
  adjust: " (変えたい人だけ入力)",
  shares: " (比率を入力)",
};

/** エラーを入力欄の直下に表示するフィールド */
const FIELDS_WITH_INLINE_ERROR: Array<keyof ExpenseFormState> = ["title", "amountText"];

export interface ExpenseFormProps {
  group: GroupDto;
  initialState?: ExpenseFormState;
  submitLabel: string;
  pending: boolean;
  onSubmit(input: ExpenseInput): void;
  onCancel?: () => void;
}

export function ExpenseForm({
  group,
  initialState,
  submitLabel,
  pending,
  onSubmit,
  onCancel,
}: ExpenseFormProps) {
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [form, setForm] = useState<ExpenseFormState>(
    () => initialState ?? createEmptyForm(group.members, today),
  );
  const [error, setError] = useState<string | null>(null);
  const [errorField, setErrorField] = useState<keyof ExpenseFormState | null>(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const titleRef = useRef<HTMLInputElement>(null);
  const amountRef = useRef<HTMLInputElement>(null);

  const debouncedTitle = useDebouncedValue(form.title, 180);
  const suggestionsQuery = useSuggestions(group.shareToken, debouncedTitle, {
    enabled: showSuggestions,
  });
  const suggestions = suggestionsQuery.data?.suggestions ?? [];

  // 「1人あたりいくらか」。金額を変えた人がいれば、残りに再配分した結果になる。
  const shares = useMemo(() => computeShares(form, group.currency), [form, group.currency]);
  const shareOf = (memberId: string): number | null =>
    shares?.find((s) => s.memberId === memberId)?.amount ?? null;

  // 金額が入るまでは「—」が並ぶだけなので、内訳そのものを出さない
  const amountEntered = useMemo(() => {
    const parsed = parseAmountInput(form.amountText, group.currency);
    return parsed !== null && parsed > 0;
  }, [form.amountText, group.currency]);

  // 分け方によらず、金額が入っていれば同じ内訳表を出す
  const showBreakdown = amountEntered && form.participantIds.length > 0;

  const choose = (suggestion: ExpenseSuggestion): void => {
    setForm((current) => applySuggestion(current, suggestion));
    setShowSuggestions(false);
    setActiveIndex(-1);
  };

  const handleTitleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (!showSuggestions || suggestions.length === 0) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((i) => (i + 1) % suggestions.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (event.key === "Enter" && activeIndex >= 0) {
      event.preventDefault();
      choose(suggestions[activeIndex]!);
    } else if (event.key === "Escape") {
      setShowSuggestions(false);
    }
  };

  const handleSubmit = (event: FormEvent): void => {
    event.preventDefault();
    const result = buildExpenseInput(form, group.currency);
    if (!result.ok) {
      setError(result.message);
      setErrorField(result.field);
      // 入力の不備は画面外にあることが多いので、その場所まで連れて行く
      const target = result.field === "title" ? titleRef.current : amountRef.current;
      if (result.field === "title" || result.field === "amountText") {
        target?.scrollIntoView({ block: "center", behavior: "smooth" });
        target?.focus({ preventScroll: true });
      }
      return;
    }
    setError(null);
    setErrorField(null);
    onSubmit(result.input);
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      {/* --- 日付 (「未指定」も選べる) --- */}
      <div className="field">
        <label className="field__label" htmlFor="expense-date">
          日付
        </label>
        <div className="row row--wrap">
          {/*
            「未指定」中も日付欄は触れるままにする。
            入力すればそのまま日付が決まり、「未指定」を押し直す手間が要らない。
          */}
          <input
            id="expense-date"
            className="input"
            type="date"
            style={{ flex: "1 1 10em" }}
            value={form.occurredOn ?? ""}
            onChange={(event) =>
              setForm({ ...form, occurredOn: event.target.value === "" ? null : event.target.value })
            }
          />
          <button
            type="button"
            className={`chip${form.occurredOn === null ? " chip--selected" : ""}`}
            aria-pressed={form.occurredOn === null}
            onClick={() =>
              setForm({ ...form, occurredOn: form.occurredOn === null ? today : null })
            }
          >
            未指定
          </button>
        </div>
      </div>

      {/* --- 支出名 (入力候補つき) --- */}
      <div className="field">
        <label className="field__label" htmlFor="expense-title">
          支出名
        </label>
        <input
          id="expense-title"
          ref={titleRef}
          className="input"
          value={form.title}
          placeholder="例: 居酒屋 / タクシー"
          autoComplete="off"
          role="combobox"
          aria-expanded={showSuggestions}
          aria-controls="expense-suggestions"
          aria-invalid={errorField === "title"}
          onChange={(event) => {
            setForm({ ...form, title: event.target.value });
            setShowSuggestions(true);
            setActiveIndex(-1);
          }}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => setShowSuggestions(false)}
          onKeyDown={handleTitleKeyDown}
        />
        {showSuggestions && (
          <div id="expense-suggestions">
            <SuggestionList
              suggestions={suggestions}
              activeIndex={activeIndex}
              onSelect={choose}
            />
          </div>
        )}
        {errorField === "title" && error && (
          <p className="field-error" role="alert">
            {error}
          </p>
        )}
      </div>

      {/* --- 金額 --- */}
      <div className="field">
        <label className="field__label" htmlFor="expense-amount">
          金額
        </label>
        <input
          id="expense-amount"
          ref={amountRef}
          className="input input--amount"
          value={form.amountText}
          inputMode="decimal"
          placeholder="例: 12000"
          aria-invalid={errorField === "amountText"}
          onChange={(event) => setForm({ ...form, amountText: event.target.value })}
        />
        {errorField === "amountText" && error && (
          <p className="field-error" role="alert">
            {error}
          </p>
        )}
      </div>

      {/* --- 立替者 --- */}
      <div className="field">
        <span className="field__label">立て替えた人</span>
        <div className="chip-row">
          {group.members.map((member) => (
            <button
              key={member.id}
              type="button"
              className={`chip${form.payerId === member.id ? " chip--selected" : ""}`}
              aria-pressed={form.payerId === member.id}
              aria-label={`立て替え: ${member.name}`}
              onClick={() => setForm({ ...form, payerId: member.id })}
            >
              {member.name}
            </button>
          ))}
        </div>
      </div>

      {/* --- 参加者 --- */}
      <div className="field">
        {/* 一括操作は見出しと同じ行の右端に、リンクとして小さく置く */}
        <div className="field__label field__label--row">
          <span>割り勘する人 ({form.participantIds.length}人)</span>
          <span className="field__actions">
            <button
              type="button"
              className="link-button"
              onClick={() => setForm({ ...form, participantIds: group.members.map((m) => m.id) })}
            >
              全員を選択
            </button>
            <button
              type="button"
              className="link-button"
              onClick={() => setForm({ ...form, participantIds: [] })}
            >
              選択を解除
            </button>
          </span>
        </div>
        <div className="chip-row">
          {group.members.map((member) => (
            <button
              key={member.id}
              type="button"
              className={`chip${form.participantIds.includes(member.id) ? " chip--selected" : ""}`}
              aria-pressed={form.participantIds.includes(member.id)}
              aria-label={`割り勘: ${member.name}`}
              onClick={() => setForm(toggleParticipant(form, member.id))}
            >
              {member.name}
            </button>
          ))}
        </div>
      </div>

      {/* --- 分け方 --- */}
      <div className="field">
        <span className="field__label">分け方</span>
        <div className="chip-row">
          {(Object.keys(SPLIT_MODE_LABELS) as SplitMode[]).map((mode) => (
            <button
              key={mode}
              type="button"
              className={`chip${form.splitMode === mode ? " chip--selected" : ""}`}
              aria-pressed={form.splitMode === mode}
              onClick={() => setForm({ ...form, splitMode: mode })}
            >
              {SPLIT_MODE_LABELS[mode]}
            </button>
          ))}
        </div>

      </div>

      {/*
        --- 1人あたりの内訳 ---
        分け方によらず同じ表を出す。均等はそのまま金額、個別に調整は金額の入力欄、
        比率で調整は比率の入力欄 + 計算後の金額。列の並びが揃うので、
        分け方を切り替えても画面の形が変わらない。
      */}
      {showBreakdown && (
        <div className="field">
          <span className="field__label">
            1人あたりの金額
            {SPLIT_MODE_HINTS[form.splitMode]}
          </span>

          <div className="share-table">
            {form.participantIds.map((memberId) => {
              const member = group.members.find((m) => m.id === memberId);
              const computed = shareOf(memberId);
              const inputValue = form.splitValues[memberId] ?? "";
              const isFixed = form.splitMode === "adjust" && inputValue.trim() !== "";
              const amountLabel =
                computed === null ? "—" : formatMoney(computed, group.currency);

              return (
                <div className="share-row" key={memberId}>
                  <span className="share-row__name">{member?.name}</span>

                  {form.splitMode === "equal" && (
                    <span className="share-row__amount">{amountLabel}</span>
                  )}

                  {form.splitMode === "adjust" && (
                    <>
                      <input
                        className="share-row__input"
                        inputMode="decimal"
                        aria-label={`${member?.name} の金額`}
                        value={inputValue}
                        placeholder={computed === null ? "" : String(computed)}
                        onChange={(event) =>
                          setForm({
                            ...form,
                            splitValues: { ...form.splitValues, [memberId]: event.target.value },
                          })
                        }
                      />
                      {isFixed ? (
                        <button
                          type="button"
                          className="button button--ghost button--small"
                          onClick={() =>
                            setForm({
                              ...form,
                              splitValues: { ...form.splitValues, [memberId]: "" },
                            })
                          }
                        >
                          自動に戻す
                        </button>
                      ) : (
                        <span className="muted">自動</span>
                      )}
                    </>
                  )}

                  {form.splitMode === "shares" && (
                    <>
                      <input
                        className="share-row__input share-row__input--ratio"
                        inputMode="decimal"
                        aria-label={`${member?.name} の比率`}
                        value={inputValue}
                        placeholder="1"
                        onChange={(event) =>
                          setForm({
                            ...form,
                            splitValues: { ...form.splitValues, [memberId]: event.target.value },
                          })
                        }
                      />
                      <span className="share-row__amount">{amountLabel}</span>
                    </>
                  )}
                </div>
              );
            })}

            <div className="share-summary">
              <span>合計</span>
              <span>
                {shares === null
                  ? "入力内容を確認してください"
                  : formatMoney(
                      shares.reduce((acc, s) => acc + s.amount, 0),
                      group.currency,
                    )}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* --- メモ (任意。あとから思い出す手がかりになるので隠さない) --- */}
      <div className="field">
        <label className="field__label" htmlFor="expense-note">
          メモ (任意)
        </label>
        <textarea
          id="expense-note"
          className="textarea"
          value={form.note}
          placeholder="例: 二次会の分も含む"
          onChange={(event) => setForm({ ...form, note: event.target.value })}
        />
      </div>


      {/* 該当フィールドの直下に出したものは、ここで二重に出さない */}
      {error && !FIELDS_WITH_INLINE_ERROR.includes(errorField ?? "note") && (
        <p className="field-error" role="alert">
          {error}
        </p>
      )}

      <div className="row row--end" style={{ marginTop: 12 }}>
        {onCancel && (
          <button type="button" className="button button--secondary" onClick={onCancel}>
            キャンセル
          </button>
        )}
        <button type="submit" className="button" disabled={pending}>
          {pending ? "保存中…" : submitLabel}
        </button>
      </div>
    </form>
  );
}
